import { DateTime } from "luxon"

// Instrument readiness / qualification, DERIVED from the app's existing Asset
// maintenance/usability data — there is NO net-new calibration collection.
//
// An asset (instrument) carries `asset.maintenance[0].endDate` (+ a `reminder` window),
// and `AssetViewScreen` already renders a RED warning triangle when that end date is in
// the past (overdue) and a YELLOW one inside the reminder window. These helpers reuse the
// exact same overdue condition so the "is this instrument usable/qualified" gate shares a
// single source of truth with that triangle.
//
// F-C2 (QC result entry) will call `isInstrumentQualified(asset)` to gate instrument
// selection — blocking only instruments whose maintenance is overdue (the red-triangle
// case), exactly as the existing usability system already flags them.

/**
 * Whether the asset's maintenance is overdue — i.e. its first maintenance record has an
 * `endDate` that parses and is strictly before now. Mirrors the `expiredAssets` condition
 * in `src/screens/AssetViewScreen/index.jsx` (the red warning triangle).
 *
 * @param {{ maintenance?: Array<{ endDate?: string }> }} asset
 * @returns {boolean} true when maintenance is overdue, false otherwise (incl. no data)
 */
export function isAssetMaintenanceExpired(asset) {
  const endDate = asset?.maintenance?.[0]?.endDate
  if (!endDate) {
    return false
  }
  const end = DateTime.fromISO(endDate)
  return end.isValid && end < DateTime.now()
}

/**
 * Whether the instrument (asset) may be selected for QC result entry (F-C2 gate).
 *
 * Derived purely from the existing maintenance data (no net-new data): an asset with no
 * maintenance record — or one whose maintenance is not overdue — is treated as
 * qualified/allowed; only an asset whose maintenance is OVERDUE (the red triangle in
 * `AssetViewScreen`) is NOT qualified. Same semantics as the existing usability warning.
 *
 * @param {{ maintenance?: Array<{ endDate?: string }> }} asset
 * @returns {boolean} true when the instrument is qualified for selection
 */
export function isInstrumentQualified(asset) {
  return !isAssetMaintenanceExpired(asset)
}
